import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Bell,
  Settings,
  LogOut,
  ChevronDown,
  CheckCircle2,
  Info,
  AlertTriangle,
  X,
  UsersRound,
  Cog,
} from 'lucide-react';
import { useSettingsStore } from '@store/settingsStore';
import { useAuthStore } from '@store/authStore';

// Detect environment — VITE_APP_ENV is injected at build time by the CI workflow
// for deployed environments. import.meta.env.DEV covers local dev.
const isDev = import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'dev';

// Mock notifications - in future this could come from an API
interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const mockNotifications: Notification[] = [
  {
    id: '1',
    type: 'info',
    title: 'Welcome to Operations Hub',
    message: 'Your account has been set up successfully.',
    time: '2 hours ago',
    read: false,
  },
  {
    id: '2',
    type: 'success',
    title: 'Beacon CRM Connected',
    message: 'Your Beacon CRM integration is active.',
    time: '1 day ago',
    read: true,
  },
];

export default function TopBar() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const avatarUrl = useSettingsStore((state) => state.avatarUrl);
  const teamRole = useAuthStore((state) => state.teamRole);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState(mockNotifications);

  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const account = accounts[0];
  const initials = account?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'U';

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setShowUserMenu(false);
    instance.logoutRedirect();
  };

  const markAllRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  const markAsRead = (id: string) => {
    setNotifications(notifications.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 h-14 flex items-center px-4 ${
        isDev ? 'bg-orange-500' : 'bg-sca-purple-600'
      }`}
    >
      {/* Spacer for mobile menu button */}
      <div className="w-10 lg:hidden" />

      {/* Logo and title */}
      <div className="flex items-center gap-3 lg:pl-0">
        <img
          src="/logo.svg"
          alt="SCA UK"
          className="h-8 w-8 object-contain"
        />
        <span className="text-white font-semibold text-lg hidden sm:block">
          Operations Hub
        </span>
        {isDev && (
          <span className="text-white/90 text-xs font-bold bg-white/20 px-2 py-0.5 rounded uppercase">
            DEV
          </span>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right side: Notifications and User Menu */}
      <div className="flex items-center gap-2">
        {/* Notification bell */}
        <div className="relative" ref={notificationRef}>
          <button
            className="relative p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Notifications"
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowUserMenu(false);
            }}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-sca-pink-500 rounded-full" />
            )}
          </button>

          {/* Notifications dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="text-xs text-sca-purple-600 hover:text-sca-purple-700"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No notifications</p>
                  </div>
                ) : (
                  <ul>
                    {notifications.map((notification) => (
                      <li
                        key={notification.id}
                        onClick={() => markAsRead(notification.id)}
                        className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 ${
                          !notification.read ? 'bg-sca-purple-50' : ''
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm ${!notification.read ? 'font-semibold' : 'font-medium'} text-gray-900`}
                            >
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-500 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                          </div>
                          {!notification.read && (
                            <div className="flex-shrink-0">
                              <span className="h-2 w-2 bg-sca-pink-500 rounded-full inline-block" />
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">System notifications appear here</p>
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            className="flex items-center gap-2 p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
            }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={account?.name || 'User'}
                className="h-7 w-7 rounded-full object-cover"
              />
            ) : (
              <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-medium">
                {initials}
              </div>
            )}
            <span className="text-sm font-medium hidden sm:block">{account?.name || 'User'}</span>
            <ChevronDown className="h-4 w-4 hidden sm:block" />
          </button>

          {/* User dropdown */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{account?.name || 'User'}</p>
                <p className="text-xs text-gray-500 truncate">{account?.username || ''}</p>
                {teamRole && (
                  <p className="text-xs text-sca-purple-600 mt-0.5 capitalize">{teamRole}</p>
                )}
              </div>
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/settings');
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </button>
                {teamRole === 'Admin' && (
                  <>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/team-management');
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <UsersRound className="h-4 w-4" />
                      <span>Team Management</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/system-settings');
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Cog className="h-4 w-4" />
                      <span>System Settings</span>
                    </button>
                  </>
                )}
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
